import type { AIAnalysis } from "../../core";
import { Clock3, Sparkles, Cpu, AlertCircle } from "lucide-react";

interface AnalysisInfoProps {
	analysis: AIAnalysis | null;
	loading: boolean;
}

const MS_IN_DAY = 1000 * 60 * 60 * 24;
const STALE_AFTER_DAYS = 1;

export default function AnalysisInfo({ analysis, loading }: AnalysisInfoProps) {
	// 1. Skeleton Loading State
	if (loading) {
		return (
			<div className="flex items-center justify-between gap-3 py-2 px-1 my-1 animate-pulse">
				<div className="flex items-center gap-2">
					<div className="h-4 w-4 rounded bg-zinc-200 dark:bg-zinc-800" />
					<div className="h-4 w-28 bg-zinc-200 dark:bg-zinc-800 rounded" />
					<div className="h-5 w-36 bg-zinc-200 dark:bg-zinc-800 rounded-full" />
				</div>
				<div className="flex items-center gap-5">
					<div className="h-4 w-28 bg-zinc-200 dark:bg-zinc-800 rounded" />
					<div className="h-4 w-24 bg-zinc-200 dark:bg-zinc-800 rounded" />
				</div>
			</div>
		);
	}

	// 2. Empty State
	if (!analysis) {
		return (
			<div className="flex items-center gap-2 py-2 px-1 my-1 text-xs text-zinc-500">
				<Sparkles size={14} className="text-zinc-400 dark:text-zinc-600" />
				<span>No AI portfolio analysis available</span>
			</div>
		);
	}

	// 3. Active State
	const analysisDateObj = new Date(analysis.date);
	const analysisDate = analysisDateObj.toLocaleDateString(undefined, {
		month: "short",
		day: "numeric",
		year: "numeric",
	});

	const analysisAgeDays = Math.max(0, Math.floor((Date.now() - analysisDateObj.getTime()) / MS_IN_DAY));
	const isAnalysisStale = analysisAgeDays > STALE_AFTER_DAYS;

	const getRelativeTimeString = (days: number) => {
		if (days === 0) return "Today";
		if (days === 1) return "Yesterday";
		return `${days} days ago`;
	};

	return (
		<div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 py-2 px-1 my-1 text-xs text-zinc-600 dark:text-zinc-400">
			{/* Left Side: Title & Descriptive Freshness Status */}
			<div className="flex items-center gap-3">
				<div className="flex items-center gap-1.5 font-medium text-zinc-800 dark:text-zinc-200">
					<Sparkles size={14} className="text-sky-600 dark:text-sky-400" />
					<span>AI Analysis</span>
				</div>

				<span
					className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium ${
						isAnalysisStale
							? "text-amber-700 bg-amber-100 dark:text-amber-400 dark:bg-amber-400/10"
							: "text-emerald-700 bg-emerald-100 dark:text-emerald-400 dark:bg-emerald-400/10"
					}`}
				>
					{isAnalysisStale ? (
						<>
							<AlertCircle size={11} />
							<span>Stale • Outdated ({getRelativeTimeString(analysisAgeDays)})</span>
						</>
					) : (
						<>
							<span className="relative flex h-1.5 w-1.5">
								<span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 dark:bg-emerald-400 opacity-75"></span>
								<span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500 dark:bg-emerald-400"></span>
							</span>
							<span>Fresh • Generated {getRelativeTimeString(analysisAgeDays)}</span>
						</>
					)}
				</span>
			</div>

			{/* Right Side: Explicitly Labeled Metadata */}
			<div className="flex items-center gap-5">
				<div className="flex items-center gap-1.5">
					<Clock3 size={13} className="text-zinc-400 dark:text-zinc-500 shrink-0" />
					<span className="text-zinc-500">Last Run:</span>
					<span className="text-zinc-700 dark:text-zinc-300 font-medium">{analysisDate}</span>
				</div>

				<div className="flex items-center gap-1.5">
					<Cpu size={13} className="text-zinc-400 dark:text-zinc-500 shrink-0" />
					<span className="text-zinc-500">Engine:</span>
					<span className="font-mono text-zinc-700 dark:text-zinc-300 font-medium">{analysis.model ?? "N/A"}</span>
				</div>
			</div>
		</div>
	);
}
